import React, {
  useState, useMemo, useEffect,
} from 'react';
import {
  Form, Tabs, Row, Divider, message,
} from 'antd';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { PlusOutlined } from '@ant-design/icons';

import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';
import OvertimeContent from './OvertimeContent';
import OTProfileAssignmentTable from './OTProfileAssignmentTable';
import OTProfileAssignDrawer from './OTProfileAssignDrawer';
import { updateOtProfile, deleteOtProfile } from '../state/settings.actions';
import CompanySettingsCard from '../CompanySettingsCard';
import {
  confirmOvertimeSettingsSave,
  getLatestSavedOvertimeSettings,
  hasOvertimeSettingsChanges,
} from '../../helpers/overtime/overtimeSettingsHelpers';

const { TabPane } = Tabs;

export default function OTProfileTab({ profile, setActiveTab }) {
  const dispatch = useDispatch();
  const users = useSelector((state) => state.users.users) || [];
  const divisions = useSelector((state) => state.settings.divisions) || {};
  const companySettings = useSelector((state) => state.settings.company?.settings) || {};
  const history = useSelector((state) => state.settings.otSettingsVersions);

  const [form] = Form.useForm();
  const [settings, setSettings] = useState(profile.settings || {});
  const [saving, setSaving] = useState(false);
  const [assignDrawerType, setAssignDrawerType] = useState(null);
  const [toAddUserIds, setToAddUserIds] = useState([]);
  const [toRemoveUserIds, setToRemoveUserIds] = useState([]);
  const [toAddDivisionIds, setToAddDivisionIds] = useState([]);
  const [toRemoveDivisionIds, setToRemoveDivisionIds] = useState([]);
  const profileId = profile.id;

  const versionHistoryEntry = history?.[profileId];
  const versionHistory = versionHistoryEntry?.versions ?? [];
  // GETs return currently-applied OT; form edits latest when a version is scheduled.
  const profileSettings = useMemo(
    () => getLatestSavedOvertimeSettings(profile.settings || {}, versionHistoryEntry || {}),
    [profile.settings, versionHistoryEntry],
  );

  useEffect(() => {
    setSettings(profileSettings);
  }, [profileSettings]);

  const assignedUsers = useMemo(
    () => users.filter((u) => u.otProfileId === profileId),
    [users, profileId],
  );

  const assignedDivisions = useMemo(
    () => Object.values(divisions).filter((d) => d && d.otProfileId === profileId),
    [divisions, profileId],
  );

  const allAssignedUsers = useMemo(() => {
    const removed = new Set(toRemoveUserIds);
    const added = new Set(toAddUserIds);
    const fromStore = assignedUsers.filter((u) => !removed.has(u.id) && !added.has(u.id));
    const fromAdd = users.filter((u) => added.has(u.id));
    return [...fromStore, ...fromAdd].filter((u) => u.active);
  }, [assignedUsers, toRemoveUserIds, toAddUserIds, users]);

  const allAssignedDivisions = useMemo(() => {
    const removed = new Set(toRemoveDivisionIds);
    const added = new Set(toAddDivisionIds);
    const divList = Object.values(divisions).filter(Boolean);
    const fromStore = assignedDivisions.filter((d) => !removed.has(d.id) && !added.has(d.id));
    const fromAdd = divList.filter((d) => added.has(d.id));
    return [...fromStore, ...fromAdd];
  }, [assignedDivisions, toRemoveDivisionIds, toAddDivisionIds, divisions]);

  const handleRemoveUser = (userId) => {
    setToAddUserIds((prev) => prev.filter((id) => id !== userId));
    setToRemoveUserIds((prev) => (
      assignedUsers.some((u) => u.id === userId) ? [...prev, userId] : prev
    ));
  };

  const handleRemoveDivision = (divisionId) => {
    setToAddDivisionIds((prev) => prev.filter((id) => id !== divisionId));
    setToRemoveDivisionIds((prev) => (
      assignedDivisions.some((d) => d.id === divisionId) ? [...prev, divisionId] : prev
    ));
  };

  const handleAssignUsers = (userIds) => {
    setToRemoveUserIds((prev) => prev.filter((id) => !userIds.includes(id)));
    setToAddUserIds((prev) => [...new Set([...prev, ...userIds])]);
  };

  const handleAssignDivisions = (divisionIds) => {
    setToRemoveDivisionIds((prev) => prev.filter((id) => !divisionIds.includes(id)));
    setToAddDivisionIds((prev) => [...new Set([...prev, ...divisionIds])]);
  };

  const performSave = async () => {
    const values = form.getFieldsValue();
    const profilePayload = { name: values.name, settings };
    const hasAssignmentChanges = toAddUserIds.length > 0 || toRemoveUserIds.length > 0
      || toAddDivisionIds.length > 0 || toRemoveDivisionIds.length > 0;
    const assignmentPayload = hasAssignmentChanges ? {
      addUserIds: toAddUserIds,
      removeUserIds: toRemoveUserIds,
      addDivisionIds: toAddDivisionIds,
      removeDivisionIds: toRemoveDivisionIds,
    } : {};

    setSaving(true);
    try {
      await dispatch(updateOtProfile(profileId, {
        ...profilePayload,
        ...assignmentPayload,
      }));
      setToAddUserIds([]);
      setToRemoveUserIds([]);
      setToAddDivisionIds([]);
      setToRemoveDivisionIds([]);
      message.success('Profile saved');
    } catch {
      message.error('Failed to save OT profile');
    }
    setSaving(false);
  };

  const handleSave = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }

    if (hasOvertimeSettingsChanges(profileSettings, settings)) {
      confirmOvertimeSettingsSave({
        companySettings,
        savedSettings: profileSettings,
        draftSettings: settings,
        versionHistory,
        onConfirm: performSave,
      });
      return;
    }

    await performSave();
  };

  const handleDelete = () => {
    CustomConfirmModal({
      title: `Delete "${profile.name}"?`,
      content: 'This profile will be removed. Users and divisions using it will fall back to company or division settings.',
      okText: 'Delete',
      cancelText: 'Cancel',
      onOk: async () => {
        await dispatch(deleteOtProfile(profileId));
        setActiveTab('company');
        message.success('OT profile deleted');
      },
    });
  };

  return (
    <div style={{ padding: '10px 20px', overflowY: 'auto', height: 'calc(90vh - 20px)', paddingBottom: 50 }}>
      <Form
        form={form}
        layout="vertical"
        initialValues={{ name: profile.name }}
      >
        <Row gutter={8} align="middle">
          <Form.Item
            name="name"
            label="Profile Name"
            rules={[{ required: true, message: 'Profile name is required' }]}
          >
            <OnTraccrTextInput style={{ maxWidth: 440 }} />
          </Form.Item>
          <OnTraccrButton
            title="Save"
            onClick={handleSave}
            loading={saving}
            style={{ marginLeft: 8, marginBottom: -5 }}
          />
          <OnTraccrButton
            title="Delete"
            type="back"
            onClick={handleDelete}
            style={{ marginBottom: -5, marginLeft: 8 }}
          />
        </Row>
      </Form>

      <Divider style={{ margin: 0 }} />

      <CompanySettingsCard
        title="Assign to users and divisions"
      >
        <Tabs defaultActiveKey="users">
          <TabPane tab="Users" key="users">
            <OnTraccrButton
              title="Add Users"
              icon={<PlusOutlined />}
              style={{ marginBottom: 8 }}
              onClick={() => setAssignDrawerType('users')}
            />
            <OTProfileAssignmentTable
              data={allAssignedUsers}
              onRemove={handleRemoveUser}
            />
          </TabPane>
          <TabPane tab="Divisions" key="divisions">
            <OnTraccrButton
              title="Add Divisions"
              icon={<PlusOutlined />}
              style={{ marginBottom: 8 }}
              onClick={() => setAssignDrawerType('divisions')}
            />
            <OTProfileAssignmentTable
              data={allAssignedDivisions}
              onRemove={handleRemoveDivision}
            />
          </TabPane>
        </Tabs>
      </CompanySettingsCard>

      <OvertimeContent
        settings={settings}
        onSettingsChange={(updates) => {
          setSettings((prev) => ({ ...prev, ...updates }));
        }}
        deferSave
        otProfileId={profileId}
      />

      {assignDrawerType && (
        <OTProfileAssignDrawer
          visible={!!assignDrawerType}
          onClose={() => setAssignDrawerType(null)}
          type={assignDrawerType}
          profileId={profileId}
          assignedIds={
            assignDrawerType === 'users'
              ? allAssignedUsers.map((u) => u.id)
              : allAssignedDivisions.map((d) => d.id)
          }
          onAssign={
            assignDrawerType === 'users' ? handleAssignUsers : handleAssignDivisions
          }
        />
      )}
    </div>
  );
}

OTProfileTab.propTypes = {
  profile: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    settings: PropTypes.shape({}),
  }).isRequired,
  setActiveTab: PropTypes.func.isRequired,
};
